const { parse, idify } = require('../../tools/smart-id')
const sanitiseManufacturer = require('../../utils/string-to-id')
const { batchIdToProductId, isBatchId, generateUnknownBatchForProduct, parseBatchId } = require('../../shipment/tools/product-batch-util')
const batchId = require('../../utils/batch-id')

exports.sanitiseManufacturer = sanitiseManufacturer
exports.batchIdToProductId = batchIdToProductId
exports.isBatchId = isBatchId
exports.generateUnknownBatchForProduct = generateUnknownBatchForProduct
exports.parseBatchId = parseBatchId

exports.sanitiseBatchNo = sanitiseBatchNo
function sanitiseBatchNo (batchNo) {
  return batchNo.split(/\s+/).join('').toUpperCase() // removes unwanted spaces and transforms it to uppercase-letters
}

exports.sanitiseBatchId = sanitiseBatchId
function sanitiseBatchId (batchId) {
  const batchIdParsed = parse(batchId)

  if (batchIdParsed.batchNo) {
    // Extract the full batchNo directly from the original string to preserve any
    // colons within the batch number itself (e.g. '5326BU1D1/SUB:1').
    // parse() treats ':' as a key-value separator, so a batchNo like
    // 'SUB:1' would be silently truncated to 'SUB' and ':1' lost.
    const batchNoMatch = batchId.match(/:batchNo:(.+)$/)
    const fullBatchNo = batchNoMatch ? batchNoMatch[1] : batchIdParsed.batchNo
    batchIdParsed.batchNo = sanitiseBatchNo(fullBatchNo)
    const idified = idify({ fields: batchIdParsed, pattern: 'product:manufacturer:batchNo', objectId: batchId })
    if (idified) { return idified }
  } else { return batchId }
}

exports.sanitiseBatchesForDuplicates = sanitiseBatchesForDuplicates
function sanitiseBatchesForDuplicates (docs) {
  let batchesMap = new Map()
  docs.forEach(batch => {
    const sanitisedBatchId = sanitiseBatchId(batch._id)
    batch._id = sanitisedBatchId
    batchesMap.set(sanitisedBatchId, batch)
  })
  return [...batchesMap.values()]
}

const isUnknownBatchId = (batchId) => !!batchId.toLowerCase().match('batchno:unknown')
exports.isUnknownBatchId = isUnknownBatchId

const isUnknownBatch = (batch) => isUnknownBatchId(batch.id)
exports.isUnknownBatch = isUnknownBatch

const batchNoFromId = batchId => {
  const {batchNo} = parseBatchId(batchId)

  if (batchNo) {
    return sanitiseBatchNo(batchNo)
  }

  return ''
}
exports.batchNoFromId = batchNoFromId

exports.prepareBatchParams = prepareBatchParams
function prepareBatchParams (params = {}) {
  let { productId, manufacturer, batchNo, expiry } = params

  // Prepare input data:
  productId = productId && decodeURIComponent(productId)
  manufacturer = manufacturer && decodeURIComponent(manufacturer).split(/\s+/).join('-').toLowerCase()
  batchNo = batchNo && decodeURIComponent(batchNo) // batchNo allows spaces, etc

  return {
    productId,
    manufacturer,
    batchNo,
    expiry
  }
}

/**
 * Classifies rows into new batches and batch updates for the warehouse stock upload.
 *
 * A row is a new batch when `batchid` is empty and `batchnumber` is non-empty.
 * The function generates a `batchId` from productId, manufacturer, and batchNo,
 * mutating the row's `batchid` in place.
 *
 * A row is an existing batch update when `batchid` is pre-filled and `expirydate`
 * is non-empty. Only the expiry date is collected from the CSV — the manufacturer
 * is derived from the existing batchId, ignoring any user-entered manufacturer
 * changes on existing rows. This is because when updating an existing batch, we do not want the _id
 * to have a different manufacturer than the manufacturer property.
 *
 * Rows that are neither new batches nor updates (placeholders, description rows)
 * are silently skipped.
 *
 * @param {Object[]} rows - data rows with { productid, batchid, batchnumber, expirydate, manufacturer }
 * @returns {{ newBatches: Object[], batchUpdates: Object[] }}
 */
exports.classifyBatchRows = classifyBatchRows
function classifyBatchRows (rows) {
  const newBatches = []
  const batchUpdates = []

  for (const row of rows) {
    if (!row.batchid && row.batchnumber) {
      row.batchid = batchId({
        productId: row.productid,
        manufacturer: sanitiseManufacturer(row.manufacturer || 'unknown'),
        batchNo: sanitiseBatchNo(row.batchnumber)
      })
      newBatches.push({
        productId: row.productid,
        manufacturer: row.manufacturer || 'unknown',
        batchNo: row.batchnumber,
        expiry: row.expirydate
      })
    } else if (row.batchid && row.expirydate) {
      batchUpdates.push({ batchId: row.batchid, expiry: row.expirydate })
    }
  }

  return { newBatches, batchUpdates }
}

exports.pgRowToCouchBatch = pgRowToCouchBatch
// Maps an avocado.batch row back into the same batch-doc shape couch has
// always returned, so agave's PG-backed batch routes stay a drop-in
// replacement for existing callers (e.g. web/src/statements/StatementsContainer.js
// keys batches by `_id`) rather than leaking Postgres's column names/types.
function pgRowToCouchBatch (row) {
  const batch = {
    _id: row.couchdb_id,
    type: 'batch',
    manufacturer: row.manufacturer,
    productId: `product:${row.product_id}`,
    expiry: toIsoString(row.expiry)
  }
  if (row.created_at) batch.createdAt = toIsoString(row.created_at)
  if (row.updated_at) batch.updatedAt = toIsoString(row.updated_at)
  if (row.created_by) batch.createdBy = row.created_by
  if (row.updated_by) batch.updatedBy = row.updated_by
  if (row.alias) batch.alias = { [row.alias]: row.code }
  return batch
}

function toIsoString (value) {
  return value instanceof Date ? value.toISOString() : value
}

exports.toBatchShape = toBatchShape
function toBatchShape (doc, alias) {
  const mapped = {
    id: doc._id,
    manufacturer: doc.manufacturer,
    productId: doc.productId,
    expiry: doc.expiry
  }
  if (doc.alias) {
    mapped.alias = doc.alias
  }
  if (alias !== 'none') {
    if (doc.alias && doc.alias[alias]) {
      const {manufacturer, batchNo} = parseBatchId(mapped.id)
      mapped.id = `product:${doc.alias[alias]}:manufacturer:${manufacturer}:batchNo:${batchNo}`
    }
  }

  return mapped
}
